import { useEffect, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import { toast } from 'react-toastify';
import { downloadFile } from '../../../utils/helpers';

// Services
import { getMethodApiCall } from "src/services/api";
import Table from "../table"; // Your existing Table component
// The new Reusable Layout
import GenericDataLayout from "src/components/custom/GenericDataLayout";
import AdminLayout from "src/layout/admin";

const OjtAttendanceLayout = () => {
  const { t } = useTranslation();

  // --- State Management ---
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [industryId, setIndustryId] = useState("all");
  const [logoutStatus, setLogoutStatus] = useState("");
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(
    moment().add(1, "day").format("YYYY-MM-DD")
  );
  const [data, setData] = useState({ data: [], meta: { totalRecords: 0 } });
  const [industryList, setIndustryList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  
  const itiInfo = JSON.parse(localStorage.getItem("loginedUser"));

  // --- API Calls ---
  const getTraineeAttendance = async () => {
    if (itiInfo) {
      setLoading(true);
      let url = `/v1/api/iti/attendance-by-iti?iti_id=${itiInfo?.iti?.id}&page=${page}&limit=${limit}&startDate=${startDate}&endDate=${endDate}`;
      if (industryId && industryId !== "all") url += `&industryId=${industryId}`;
      if (logoutStatus) url += `&logout_status=${logoutStatus}`;

      let res = await getMethodApiCall(url);
      if (res?.status === 200) {
        setData(res?.data);
      }
      setLoading(false);
    }
  };

  const getIndustries = async () => {
    let url = `/v1/api/trainee/industries_by_iti/${itiInfo?.iti?.id}`;
    let res = await getMethodApiCall(url);
    if (res?.status === 200) {
      setIndustryList(res.data.data);
    }
  };

  useEffect(() => {
    getIndustries();
  }, []);

  useEffect(() => {
    getTraineeAttendance();
    // eslint-disable-next-line
  }, [page, limit, startDate, endDate, industryId, logoutStatus]);

  // --- Handlers ---
  const handleClearFilters = () => {
    setPage(1);
    setLimit(10);
    setIndustryId("");
    setLogoutStatus("");
    setStartDate(moment().format("YYYY-MM-DD"));
    setEndDate(moment().add(1, "day").format("YYYY-MM-DD"));
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      let url = `/v1/api/iti/attendance-by-iti/download?iti=${itiInfo?.iti?.id}`;
      if (industryId && industryId !== "all") url += `&industryId=${industryId}`;
      if (logoutStatus) url += `&logout_status=${logoutStatus}`;
      const res = await getMethodApiCall(url, {},
            { responseType: 'blob' });

      downloadFile({
        data: res.data,
        mimeType:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        filePrefix: `attendance_${itiInfo?.iti?.iti_name || 'data'}_${moment().format("YYYYMMDD_HHmmss")}`,
        extension: "xlsx",
      });
      toast.success('File downloaded');
    } catch(error) {
      console.error(error);
    } finally{
      setIsDownloading(false);
    }
  };

  // --- Configuration Objects for the Generic Layout ---

  // 1. Defined Actions (Top Right Buttons)
  const pageActions = [
    {
      label: t("itiHome.button1") || "Academic Calendar",
      href: "https://docs.google.com/spreadsheets/d/16HgkGLfoscfxvhxr9EmLB5Ul8zXgjT_k3cRNsleQBKs/edit?gid=0#gid=0",
      target: "_blank",
      icon: ExternalLink,
    },
    {
      label: t("itiHome.button2") || "Management Committee",
      href: "https://docs.google.com/forms/d/e/1FAIpQLSd5Bm4jSzTHtYZC1rKZ6ONbcuiv_gz_VoAwpPeenSAmtx5pFw/viewform",
      target: "_blank",
      icon: ExternalLink,
    },
  ];

  // 2. Filter Configuration
  const filterConfig = [
    {
      type: "date",
      label: t('attendance.startDate'),
      value: startDate,
      onChange: (startDate) => {
        setStartDate(startDate);
        setPage(1);
      },
      max: endDate
    },
    {
      type: "date",
      label: t('attendance.endDate'),
      value: endDate,
      onChange: (endDate) => {
        setEndDate(endDate);
        setPage(1);
      },
      min: startDate
    },
    {
      type: "select",
      label: t("itiHome.industryList") || "Industry",
      value: industryId,
      onChange: (industryId) => {
        setIndustryId(industryId);
        setPage(1);
      },
      options: industryList.map((unit) => ({
        label: unit.industry_name,
        value: unit.id,
      })),
    }
  ];

  // 3. Pagination Configuration
  const paginationConfig = {
    page: page,
    limit: limit,
    totalRecords: data?.meta?.totalRecords || 0,
    onPageChange: setPage,
    onLimitChange: setLimit,
  };

  return (
    <AdminLayout>
      <GenericDataLayout
        title={`${itiInfo?.iti?.iti_name} ITI` || "ITI Dashboard"}
        subtitle={t("itiHome.title") || "OJT Attendance"}
        actions={pageActions}
        filters={filterConfig}
        onResetFilters={handleClearFilters}
        onDownload={handleDownload}
        pagination={paginationConfig}
        isLoading={loading}
        isDownloading={isDownloading}
      >
        <Table data={data} isLoading={loading} pagination={paginationConfig}/>
      </GenericDataLayout>
    </AdminLayout>
  );
};

export default OjtAttendanceLayout;